/**
 * authGate — Iter 292.6 F-C.
 *
 * A single global axios response interceptor that catches 401 responses,
 * clears local auth state, and redirects to /login with a reason query
 * parameter so the login screen can show the right message.
 *
 * Guardrails:
 *   • Never installed more than once (idempotent installer).
 *   • Only intercepts responses from the salon's own backend origin — never
 *     third-party APIs that might legitimately 401 (Stripe, Twilio, etc.).
 *   • Handles the "many-parallel-requests all 401 at once" case with a
 *     redirect-once guard so we never re-navigate on every response.
 *   • Skips the /api/auth/login endpoint itself — a failed login SHOULD
 *     surface its 401 as a form error, not a hard redirect.
 */
import axios from "axios";

const REDIRECT_ONCE = { firedAt: 0 };
let INSTALLED = false;

// A minute of quiet after a forced redirect is enough to prevent re-fires
// from later-arriving parallel 401s.
const REDIRECT_COOLDOWN_MS = 60 * 1000;

const isBackendUrl = (url) => {
  if (!url) return false;
  try {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    if (!backend) return false;
    // Handle absolute + relative URLs. axios usually returns absolute.
    if (typeof url === "string" && url.startsWith(backend)) return true;
    // Fallback — treat "/api/..." paths as backend calls.
    if (typeof url === "string" && url.startsWith("/api/")) return true;
  } catch { /* ignore */ }
  return false;
};

const isLoginEndpoint = (url) => {
  if (!url) return false;
  return /\/api\/auth\/login\b/.test(String(url));
};

/** Iter 292.6 F-C — safe list of paths we can restore after re-login.
 *  Explicitly excludes write-in-progress surfaces so we never drop a
 *  half-completed booking / cleanup / release commit back on the user. */
const SAFE_RETURN_PATTERNS = [
  /^\/portal$/,
  /^\/portal\/home$/,
  /^\/portal\/calendars$/,
  /^\/portal\/receptionist$/,
  /^\/portal\/receptionist\/redundant$/,
];

/** @param {string} pathname */
export function isSafeReturnPath(pathname) {
  return SAFE_RETURN_PATTERNS.some((rx) => rx.test(pathname));
}

/**
 * Clear auth state and redirect to /login with a reason. Idempotent —
 * repeat calls within the cooldown window are no-ops.
 *
 * @param {"idle_timeout"|"session_expired"|"revoked"} reason
 */
export function forceSignOut(reason = "session_expired") {
  const now = Date.now();
  if (now - REDIRECT_ONCE.firedAt < REDIRECT_COOLDOWN_MS) return;
  REDIRECT_ONCE.firedAt = now;
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch { /* ignore */ }
  try {
    const currentPath = window.location.pathname;
    const params = new URLSearchParams();
    params.set("reason", reason);
    if (isSafeReturnPath(currentPath)) params.set("returnTo", currentPath);
    // Full navigation (not react-router) — guarantees a clean React tree
    // for the fresh session and drops any in-flight component state.
    window.location.replace(`/login?${params.toString()}`);
  } catch { /* ignore */ }
}

/** Install the global axios 401 interceptor. Idempotent. */
export function installAuthGate() {
  if (INSTALLED) return;
  INSTALLED = true;
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const status = error?.response?.status;
        const url = error?.config?.url || error?.response?.config?.url;
        if (status === 401 && isBackendUrl(url) && !isLoginEndpoint(url)) {
          // F-B: backend idle revocation signals via an "inactivity" detail —
          // surface the same reason copy as the frontend idle timer.
          const detail = error?.response?.data?.detail;
          const reason = typeof detail === "string" && /inactivity/i.test(detail)
            ? "idle_timeout"
            : "session_expired";
          forceSignOut(reason);
        }
      } catch { /* never throw from interceptor */ }
      return Promise.reject(error);
    }
  );
}

/** @internal exposed for tests */
export function __resetAuthGateForTests() {
  REDIRECT_ONCE.firedAt = 0;
}
